import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from '@/lib/utils'

interface Props {
  images: { src: string; caption?: string }[]
  intervalMs?: number
  aspect?: 'square' | '4/5' | '16/10'
  className?: string
}

const aspectClass: Record<NonNullable<Props['aspect']>, string> = {
  square: 'aspect-square',
  '4/5': 'aspect-[4/5]',
  '16/10': 'aspect-[16/10]',
}

/** Auto-rotating image carousel with fade + Ken Burns, dots at the bottom, hover to pause. */
export function ImageCarousel({ images, intervalMs = 4500, aspect = '4/5', className }: Props) {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (paused || images.length < 2) return
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % images.length), intervalMs)
    return () => clearTimeout(t)
  }, [index, paused, images.length, intervalMs])

  if (!images.length) return null
  const active = images[index]

  return (
    <div
      className={cn('relative overflow-hidden rounded-2xl bg-pitch-800', aspectClass[aspect], className)}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <AnimatePresence mode="sync">
        <motion.div
          key={active.src}
          initial={{ opacity: 0, scale: 1.02 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="absolute inset-0"
        >
          <motion.img
            src={active.src}
            alt={active.caption ?? ''}
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: 1.08 }}
            transition={{ duration: intervalMs / 1000, ease: 'linear' }}
          />
        </motion.div>
      </AnimatePresence>

      {/* Bottom gradient + optional caption */}
      <div className="absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-pitch-950/85 to-transparent pointer-events-none" />

      {active.caption && (
        <div className="absolute bottom-16 left-5 right-5 z-10">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.src + '-cap'}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.4, delay: 0.25 }}
              className="text-white font-display uppercase tracking-wide text-sm md:text-base [text-shadow:_0_2px_10px_rgba(0,0,0,0.7)]"
            >
              {active.caption}
            </motion.div>
          </AnimatePresence>
        </div>
      )}

      {/* Dots */}
      <div className="absolute bottom-5 left-1/2 -translate-x-1/2 flex gap-2 z-10">
        {images.map((img, i) => (
          <button
            key={img.src}
            onClick={() => setIndex(i)}
            aria-label={`Show image ${i + 1}`}
            className={cn(
              'h-1.5 rounded-full transition-all',
              i === index ? 'w-8 bg-cosmos-gold' : 'w-1.5 bg-white/50 hover:bg-white/80'
            )}
          />
        ))}
      </div>
    </div>
  )
}
